import { NavLink, Outlet, Link, useLocation } from 'react-router-dom';
import {
  Building2,
  Inbox,
  LayoutDashboard,
  Lightbulb,
  LogOut,
  Megaphone,
  UsersRound,
} from 'lucide-react';
import { useAuth } from '../state/AuthContext';
import { paths } from '../lib/paths';
import { ThemeToggle } from './ThemeToggle';
import { ScorekeeperIcon } from './ScorekeeperLogo';

const COMPANY_NAV = [
  { to: paths.companyHome, label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: paths.companyPipeline, label: 'Pipeline', icon: Inbox },
  { to: paths.companyContacts, label: 'Contacts', icon: UsersRound },
  { to: paths.companyCustomers, label: 'Customers', icon: UsersRound },
  { to: paths.companyMarketing, label: 'Marketing/SEO', icon: Megaphone },
  { to: paths.companyFeedback, label: 'Product feedback', icon: Lightbulb },
];

export function CompanyShell() {
  const { googleEmail, canSuperAdmin, authReady, isSignedIn, publicHqAccess } = useAuth();
  const location = useLocation();
  const active = COMPANY_NAV.find((item) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to),
  );
  const showNav = authReady && (isSignedIn || publicHqAccess) && canSuperAdmin;
  const accountLabel = googleEmail ?? (publicHqAccess ? 'Public QA access' : 'Not signed in');

  return (
    <div className={`app-shell ${showNav ? '' : 'app-shell--no-sidebar'}`}>
      {showNav && (
        <aside className="app-shell__sidebar" aria-label="Scorekeeper HQ navigation">
          <Link to={paths.companyHome} className="brand" aria-label="Scorekeeper HQ home">
            <div className="brand__mark brand__mark--asset" aria-hidden>
              <ScorekeeperIcon />
            </div>
            <div className="brand__text">
              <div className="brand__name">Scorekeeper HQ</div>
              <div className="brand__sub">Company</div>
            </div>
          </Link>

          <nav className="nav" aria-label="Company sections">
            <div className="nav__group-label">Company</div>
            {COMPANY_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `nav__item ${isActive ? 'is-active' : ''}`}
              >
                <item.icon className="nav__icon" />
                <span>{item.label}</span>
              </NavLink>
            ))}

            <div className="nav__group-label" style={{ marginTop: 18 }}>Switch</div>
            <NavLink to={paths.adminHome} className="nav__item">
              <LayoutDashboard className="nav__icon" />
              <span>League HQ</span>
            </NavLink>
            <NavLink to={paths.marketingHome} className="nav__item">
              <Building2 className="nav__icon" />
              <span>Public site</span>
            </NavLink>
          </nav>

          <div className="sidebar-footer">
            <div className="eyebrow" style={{ marginBottom: 6 }}>Signed in as</div>
            <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {accountLabel}
            </div>
            <div className="topbar__hint" style={{ lineHeight: 1.2 }}>{publicHqAccess ? 'Testing access' : 'Super admin'}</div>
            {isSignedIn && (
              <a href="/api/auth/logout?return_to=/" className="btn btn--ghost btn--sm" style={{ width: '100%', marginTop: 10 }}>
                <LogOut size={14} /> Sign out
              </a>
            )}
          </div>
        </aside>
      )}

      <header className="app-shell__topbar">
        <div className="topbar__title">
          <h1>{active?.label ?? 'Scorekeeper HQ'}</h1>
          <span className="muted" style={{ fontSize: 12 }}>
            Run the company side of The Scorekeeper
          </span>
        </div>
        <div className="topbar__spacer" />
        <ThemeToggle />
        {isSignedIn && (
          <a href="/api/auth/logout?return_to=/" className="btn btn--ghost btn--sm">
            <LogOut size={14} /> Sign out
          </a>
        )}
      </header>

      <main className="app-shell__main">
        <div className="page">
          <Outlet />
        </div>
      </main>

      {showNav && (
        <nav className="bottom-nav" aria-label="Scorekeeper HQ mobile navigation">
          <ul className="bottom-nav__list">
            {COMPANY_NAV.map((item) => (
              <li key={item.to} style={{ flex: 1 }}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `bottom-nav__item ${isActive ? 'is-active' : ''}`}
                >
                  <item.icon size={20} />
                  <span>{item.label === 'Product feedback' ? 'Feedback' : item.label === 'Marketing/SEO' ? 'Growth' : item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
